/**
 * Axios Instance with JWT Interceptors for Wadakaroo
 *
 * Features:
 * - Automatically adds Authorization header to all requests
 * - Handles 401 responses by refreshing the token (reactive refresh)
 * - Queues failed requests during token refresh (single-flight)
 * - Redirects to login on refresh failure
 *
 * NOTE: Proactive refresh (well before token expiry, while the user is
 * active) is handled separately by useSessionManager.js. This interceptor
 * is the safety net for unexpected 401s — e.g. a request fired moments
 * before proactive refresh completed.
 */

import axios from 'axios';
import {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearAuth,
    parseToken,
    setUser,
    broadcastLogout,
} from './authService';
import { reportNetworkFailure, reportNetworkSuccess } from '../pwa/useOnlineStatus';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// ============================================
// Request Interceptor
// ============================================

axiosInstance.interceptors.request.use(
    async (config) => {
        const publicEndpoints = [
            '/auth/login',
            '/auth/token',
            '/auth/register',
            '/user/register',
            '/auth/forgot-password',
            '/auth/reset-password/confirm',
        ];

        const isPublicEndpoint = publicEndpoints.some(endpoint =>
            config.url?.includes(endpoint)
        );

        if (!isPublicEndpoint) {
            const token = getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        console.log(`🔑 [${config.method?.toUpperCase()}] ${config.url}`);
        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// ============================================
// Response Interceptor
// ============================================

axiosInstance.interceptors.response.use(
    (response) => {
        console.log(`✅ [${response.status}] ${response.config.url}`);
        // A completed round trip is the only proof the workshop's wifi actually
        // has an uplink — see useOnlineStatus for why navigator.onLine isn't.
        reportNetworkSuccess();
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        console.error(`❌ [${error.response?.status}] ${originalRequest?.url}:`,
            error.response?.data?.message || error.message);

        // No response object at all means the request never reached the server
        // (DNS, TCP, TLS, or the request being aborted mid-flight). An HTTP
        // error — including a 502 — proves connectivity, so only the former
        // raises the offline banner.
        if (error.response) {
            reportNetworkSuccess();
        } else if (error.code !== 'ERR_CANCELED') {
            reportNetworkFailure();
        }

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        const authEndpoints = ['/auth/login', '/auth/token', '/auth/logout', '/auth/register'];
        const isAuthEndpoint = authEndpoints.some(endpoint =>
            originalRequest.url?.includes(endpoint)
        );

        if (isAuthEndpoint) {
            return Promise.reject(error);
        }

        // Another request is already refreshing — queue this one and resume
        // with the fresh token when the refresh resolves.
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axiosInstance(originalRequest);
            }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            console.log('🔓 No refresh token available, clearing auth');
            clearAuth();
            broadcastLogout('no_refresh_token');
            isRefreshing = false;
            window.location.href = (import.meta.env.VITE_BASE_URL || '/wadakaroo') + '/login';
            return Promise.reject(error);
        }

        try {
            console.log('🔄 Attempting to refresh token...');

            // Bare axios (not axiosInstance) to avoid recursion through this
            // very interceptor if the refresh call itself 401s.
            const response = await axios.post(`${API_BASE_URL}/auth/token`, {
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
            }, {
                headers: { 'Content-Type': 'application/json' },
            });

            const { accessToken, refreshToken: newRefreshToken } = response.data;
            console.log('✅ Token refreshed successfully');

            setTokens(accessToken, newRefreshToken);

            const tokenUser = parseToken(accessToken);
            if (tokenUser) {
                setUser(tokenUser);
            }

            processQueue(null, accessToken);

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return axiosInstance(originalRequest);

        } catch (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            processQueue(refreshError, null);
            clearAuth();
            broadcastLogout('refresh_failed');
            window.location.href = (import.meta.env.VITE_BASE_URL || '/wadakaroo') + '/login';
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default axiosInstance;